import React from 'react';
import { CheckCircle2, Clock, CircleDot, Circle } from 'lucide-react';

/**
 * PaymentStatus - Visual status indicator for payment state
 * Redesigned with premium glass-morphism medical UI
 */
export function PaymentStatus({ status, paymentMethod, paidAmount, assignedAmount }) {
  const isActive = status === 'ACTIVE';
  const isPending = status === 'PAYMENT_PENDING';
  const isCompleted = status === 'PAYMENT_COMPLETED';

  const getStatusConfig = () => {
    if (isCompleted) {
      return {
        icon: <CheckCircle2 size={24} color="#fff" />,
        label: 'Payment Completed',
        color: '#10b981',
        gradient: 'linear-gradient(135deg, #10b981, #059669)',
        lightBg: 'rgba(16,185,129,0.06)',
        border: 'rgba(16,185,129,0.2)',
        glow: '0 0 20px rgba(16,185,129,0.15)',
        description: `${paymentMethod} · ₹${parseFloat(paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        pill: 'CLEARED',
        pillBg: 'rgba(16,185,129,0.12)',
      };
    } else if (isPending) {
      return {
        icon: <Clock size={24} color="#fff" />,
        label: 'Awaiting Payment',
        color: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
        lightBg: 'rgba(245,158,11,0.06)',
        border: 'rgba(245,158,11,0.25)',
        glow: '0 0 20px rgba(245,158,11,0.12)',
        description: `Balance due: ₹${(parseFloat(assignedAmount || 0) - parseFloat(paidAmount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        pill: 'PENDING',
        pillBg: 'rgba(245,158,11,0.12)',
      };
    } else if (isActive) {
      return {
        icon: <CircleDot size={24} color="#fff" />,
        label: 'Active Case',
        color: '#2563eb',
        gradient: 'linear-gradient(135deg, #2563eb, #1e40af)',
        lightBg: 'rgba(14,165,233,0.06)',
        border: 'rgba(14,165,233,0.2)',
        glow: '0 0 20px rgba(14,165,233,0.12)',
        description: 'Ready for consultation',
        pill: 'ACTIVE',
        pillBg: 'rgba(14,165,233,0.12)',
      };
    }
    return {
      icon: <Circle size={24} color="#fff" />,
      label: status,
      color: '#94a3b8',
      gradient: 'linear-gradient(135deg, #94a3b8, #64748b)',
      lightBg: 'rgba(148,163,184,0.06)',
      border: 'rgba(148,163,184,0.2)',
      glow: 'none',
      description: '',
      pill: status,
      pillBg: 'rgba(148,163,184,0.1)',
    };
  };

  const config = getStatusConfig();

  return (
    <div style={{
      position: 'relative',
      background: config.lightBg,
      border: `1px solid ${config.border}`,
      borderRadius: '16px',
      padding: '18px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      overflow: 'hidden',
      boxShadow: config.glow,
      backdropFilter: 'blur(8px)',
    }}>
      {/* Decorative background bar */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        background: config.gradient,
        borderRadius: '16px 0 0 16px',
      }} />

      {/* Icon orb */}
      <div style={{
        width: '46px',
        height: '46px',
        borderRadius: '13px',
        background: config.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isCompleted ? '20px' : '22px',
        fontWeight: 700,
        color: '#fff',
        boxShadow: `0 4px 14px ${config.color}40`,
        flexShrink: 0,
        fontFamily: 'serif',
        letterSpacing: '-1px',
      }}>
        {config.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{
            fontSize: '14px',
            fontWeight: 700,
            color: config.color,
            letterSpacing: '-0.3px',
          }}>
            {config.label}
          </span>
          <span style={{
            fontSize: '9px',
            fontWeight: 800,
            color: config.color,
            background: config.pillBg,
            padding: '2px 7px',
            borderRadius: '99px',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
          }}>
            {config.pill}
          </span>
        </div>
        {config.description && (
          <div style={{
            fontSize: '12px',
            color: '#64748b',
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontWeight: 500,
          }}>
            {config.description}
          </div>
        )}
      </div>

      {/* Decorative corner glow */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${config.color}15, transparent 70%)`,
        pointerEvents: 'none',
      }} />
    </div>
  );
}

